import React, { useRef, useState } from "react";
import "./registerCard.css";
import { signupUser } from "../../../services/authAPI.js";
import { Alert, Button, CircularProgress } from "@mui/material";
import Navbar from "../../common/navBar/navBar.tsx";
import {
  GENDER_OPTIONS,
  MOTHER_TONGUE_OPTIONS,
  MARITAL_STATUS_OPTIONS,
  CHILDREN_OPTIONS,
  KIDS_LIVE_WITH_OPTIONS,
  RELIGION_OPTIONS,
  CASTE_OPTIONS,
  ZODIAC_OPTIONS,
  STAR_OPTIONS,
  BODY_TYPE_OPTIONS,
  PHYSICAL_STATUS_OPTIONS,
  HEIGHT_OPTIONS,
  WEIGHT_OPTIONS,
  COMPLEXION_OPTIONS,
  COUNTRY_OPTIONS,
  RESIDENT_STATUS_OPTIONS,
  SEVVAI_LAKNA_OPTIONS,
  EATING_HABIT_OPTIONS,
  DRINKING_HABIT_OPTIONS,
  SMOKING_HABIT_OPTIONS,
  STAR_PATHAM_OPTIONS,
  PROFILE_CREATED_BY_OPTIONS,
} from "../../../utils/selectOptions.js";
import { validateAllFields, validateField } from "../../../validations/validationSchema.js";
import Footer from "../../common/footer/footer.tsx";

// Reusable select component
const SelectField = ({
  label,
  name,
  options,
  formData,
  fieldErrors,
  handleChange,
  handleBlur,
  renderError,
  placeholder = "Select",
}) => (
  <div className='form-group'>
    <label className='form-label'>{label}</label>
    <select
      name={name}
      className={`form-input${fieldErrors[name] ? " input-error" : ""}`}
      value={formData[name]}
      onChange={handleChange}
      onBlur={handleBlur}
    >
      <option value=''>{placeholder}</option>
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
    {renderError(name)}
  </div>
);

const RegisterCard = () => {
  const openLoginRef = useRef(null);

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    password: "",
    mobile: "",
    dob: "",
    birthTime: "",
    gender: "",
    motherTongue: "",
    profileCreatedBy: "",
    maritalStatus: "",
    children: "",
    kidsLiveWith: "",
    religion: "",
    caste: "",
    zodiac: "",
    star: "",
    starPatham: "",
    bodyType: "",
    physicalStatus: "",
    height: "",
    weight: "",
    complexion: "",
    birthCountry: "",
    residentCountry: "",
    residentStatus: "",
    city: "",
    address: "",
    education: "",
    occupation: "",
    fatherPlace: "",
    motherPlace: "",
    fatherCaste: "",
    motherCaste: "",
    fatherOccupation: "",
    motherOccupation: "",
    hobbies: "",
    eating: "",
    drinking: "",
    smoking: "",
    kiraga: "",
    sevvaiLakna: "",
    about: "",
    partner: "",
    photo: null,
    horoscope: null,
    profileStatus: "inactive",
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [fileInputKey, setFileInputKey] = useState(Date.now());
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;

    // ✅ handle file inputs separately
    const newValue = type === "file" ? files[0] : value;

    setFormData({ ...formData, [name]: newValue });
    if (fieldErrors[name]) setFieldErrors({ ...fieldErrors, [name]: null });
    setError(null);
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    setFieldErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      const errors = validateAllFields(formData);
      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        setLoading(false);
        return;
      }

      // ✅ Terms validation
      if (!acceptedTerms) {
        setError("You must accept the Terms & Conditions before registering.");
        setLoading(false);
        return;
      }

      const response = await signupUser(formData);

      // ── Store tokens ─────────────────────────────────────────
      if (response.accessToken) {
        localStorage.setItem("accessToken", response.accessToken);
        localStorage.setItem("refreshToken", response.refreshToken);
      }

      // ── Reset form ───────────────────────────────────────────
      setFieldErrors({});
      setFileInputKey(Date.now());
      setFormData({
        fullName: "",
        email: "",
        password: "",
        mobile: "",
        dob: "",
        birthTime: "",
        gender: "",
        motherTongue: "",
        profileCreatedBy: "",
        maritalStatus: "",
        children: "",
        kidsLiveWith: "",
        religion: "",
        caste: "",
        zodiac: "",
        star: "",
        starPatham: "",
        bodyType: "",
        physicalStatus: "",
        height: "",
        weight: "",
        complexion: "",
        birthCountry: "",
        residentCountry: "",
        residentStatus: "",
        city: "",
        address: "",
        education: "",
        occupation: "",
        fatherPlace: "",
        motherPlace: "",
        fatherCaste: "",
        motherCaste: "",
        fatherOccupation: "",
        motherOccupation: "",
        hobbies: "",
        eating: "",
        drinking: "",
        smoking: "",
        kiraga: "",
        sevvaiLakna: "",
        about: "",
        partner: "",
        photo: null,
        horoscope: null,
        profileStatus: "inactive",
      });

      setTimeout(() => {
        setSuccess(false);
        openLoginRef.current?.();
      }, 2000);
    } catch (err) {
      setError(err.message || "An error occurred during registration");
    } finally {
      setLoading(false);
    }
  };

  const renderError = (name) =>
    fieldErrors[name] ? <span className='field-error-msg'>{fieldErrors[name]}</span> : null;

  const selectProps = { formData, fieldErrors, handleChange, handleBlur, renderError };

  return (
    <>
      <Navbar
        onOpenLogin={(fn) => {
          openLoginRef.current = fn;
        }}
      />
      <div className='register-page'>
        <div className='register-container'>
          <h2 className='register-title'>Register Your Profile</h2>

          {error && (
            <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity='success' sx={{ mb: 2 }}>
              Registration successful! You can now sign in.
            </Alert>
          )}

          <div className='form-main-grid'>
            {/* LEFT COLUMN */}
            <div className='form-column'>
              <div className='form-group'>
                <label className='form-label'>Full Name</label>
                <input
                  type='text'
                  name='fullName'
                  className={`form-input${fieldErrors.fullName ? " input-error" : ""}`}
                  value={formData.fullName}
                  placeholder='Enter your full name'
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {renderError("fullName")}
              </div>

              <div className='form-group'>
                <label className='form-label'>Email</label>
                <input
                  type='email'
                  name='email'
                  className={`form-input${fieldErrors.email ? " input-error" : ""}`}
                  value={formData.email}
                  placeholder='Enter your email'
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {renderError("email")}
              </div>

              <div className='form-row'>
                <div className='form-group'>
                  <label className='form-label'>Password</label>
                  <input
                    type='password'
                    name='password'
                    className={`form-input${fieldErrors.password ? " input-error" : ""}`}
                    value={formData.password}
                    placeholder='Enter a strong password'
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {renderError("password")}
                </div>
                <div className='form-row'>
                  <div className='form-group'>
                    <label className='form-label'>Date of Birth</label>
                    <input
                      type='date'
                      name='dob'
                      className={`form-input${fieldErrors.dob ? " input-error" : ""}`}
                      value={formData.dob}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    {renderError("dob")}
                  </div>

                  <div className='form-group'>
                    <label className='form-label'>Time of Birth</label>
                    <input
                      type='time'
                      name='birthTime'
                      className={`form-input${fieldErrors.birthTime ? " input-error" : ""}`}
                      value={formData.birthTime || ""}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    {renderError("birthTime")}
                  </div>
                </div>
              </div>

              <div className='form-row'>
                <SelectField label="I'm looking for" name='gender' options={GENDER_OPTIONS} {...selectProps} />
                <SelectField
                  label='Mother Tongue'
                  name='motherTongue'
                  options={MOTHER_TONGUE_OPTIONS}
                  {...selectProps}
                />
              </div>

              <div className='form-row'>
                <SelectField
                  label='Profile Created By'
                  name='profileCreatedBy'
                  options={PROFILE_CREATED_BY_OPTIONS}
                  {...selectProps}
                />
                <SelectField
                  label='Marital Status'
                  name='maritalStatus'
                  options={MARITAL_STATUS_OPTIONS}
                  {...selectProps}
                />
              </div>

              <div className='form-row'>
                <SelectField label='No. of Children' name='children' options={CHILDREN_OPTIONS} {...selectProps} />
                <SelectField
                  label='Kids Live With'
                  name='kidsLiveWith'
                  options={KIDS_LIVE_WITH_OPTIONS}
                  {...selectProps}
                />
              </div>

              <div className='form-row'>
                <SelectField label='Religion' name='religion' options={RELIGION_OPTIONS} {...selectProps} />
                <SelectField label='Caste' name='caste' options={CASTE_OPTIONS} {...selectProps} />
              </div>

              <div className='form-row'>
                <SelectField label='Zodiac (Rasi)' name='zodiac' options={ZODIAC_OPTIONS} {...selectProps} />
                <div className='form-row'>
                  <div className='form-group'>
                    <SelectField label='Star' name='star' options={STAR_OPTIONS} {...selectProps} />
                  </div>
                  <div className='form-group'>
                    <SelectField
                      label='Star Patham'
                      name='starPatham'
                      options={STAR_PATHAM_OPTIONS}
                      {...selectProps}
                      placeholder='Select Patham'
                    />
                  </div>
                </div>
              </div>

              <div className='form-row'>
                <SelectField label='Body Type' name='bodyType' options={BODY_TYPE_OPTIONS} {...selectProps} />
                <SelectField
                  label='Physical Status'
                  name='physicalStatus'
                  options={PHYSICAL_STATUS_OPTIONS}
                  {...selectProps}
                />
              </div>

              <div className='form-row'>
                <SelectField label='Height' name='height' options={HEIGHT_OPTIONS} {...selectProps} />
                <SelectField label='Weight' name='weight' options={WEIGHT_OPTIONS} {...selectProps} />
              </div>

              <div className='form-row'>
                <SelectField label='Complexion' name='complexion' options={COMPLEXION_OPTIONS} {...selectProps} />
                <SelectField label='Birth Country' name='birthCountry' options={COUNTRY_OPTIONS} {...selectProps} />
              </div>

              <div className='form-row'>
                <SelectField
                  label='Resident Country'
                  name='residentCountry'
                  options={COUNTRY_OPTIONS}
                  {...selectProps}
                />
                <SelectField
                  label='Resident Status'
                  name='residentStatus'
                  options={RESIDENT_STATUS_OPTIONS}
                  {...selectProps}
                />
              </div>

              <div className='form-row'>
                <div className='form-group'>
                  <label className='form-label'>Residence City</label>
                  <input
                    type='text'
                    name='city'
                    className={`form-input${fieldErrors.city ? " input-error" : ""}`}
                    value={formData.city}
                    placeholder='Enter residence city'
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {renderError("city")}
                </div>
                <div className='form-group'>
                  <label className='form-label'>Address</label>
                  <input
                    type='text'
                    name='address'
                    className={`form-input${fieldErrors.address ? " input-error" : ""}`}
                    value={formData.address}
                    placeholder='Enter address'
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {renderError("address")}
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN */}
            <div className='form-column'>
              <div className='form-row'>
                <div className='form-group'>
                  <label className='form-label'>Educational Qualification</label>
                  <input
                    type='text'
                    name='education'
                    className={`form-input${fieldErrors.education ? " input-error" : ""}`}
                    value={formData.education}
                    placeholder='Enter educational qualification'
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {renderError("education")}
                </div>
                <div className='form-group'>
                  <label className='form-label'>Occupation</label>
                  <input
                    type='text'
                    name='occupation'
                    className={`form-input${fieldErrors.occupation ? " input-error" : ""}`}
                    value={formData.occupation}
                    placeholder='Enter occupation'
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {renderError("occupation")}
                </div>
              </div>

              <div className='form-row'>
                <div className='form-group'>
                  <label className='form-label'>Father's Native Place</label>
                  <input
                    type='text'
                    name='fatherPlace'
                    className={`form-input${fieldErrors.fatherPlace ? " input-error" : ""}`}
                    value={formData.fatherPlace}
                    placeholder="Enter father's native place"
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {renderError("fatherPlace")}
                </div>
                <div className='form-group'>
                  <label className='form-label'>Mother's Native Place</label>
                  <input
                    type='text'
                    name='motherPlace'
                    className={`form-input${fieldErrors.motherPlace ? " input-error" : ""}`}
                    value={formData.motherPlace}
                    placeholder="Enter mother's native place"
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {renderError("motherPlace")}
                </div>
              </div>

              <div className='form-row'>
                <SelectField label='Father Caste' name='fatherCaste' options={CASTE_OPTIONS} {...selectProps} />
                <SelectField label='Mother Caste' name='motherCaste' options={CASTE_OPTIONS} {...selectProps} />
              </div>

              <div className='form-row'>
                <div className='form-group'>
                  <label className='form-label'>Father Occupation</label>
                  <input
                    type='text'
                    name='fatherOccupation'
                    className={`form-input${fieldErrors.fatherOccupation ? " input-error" : ""}`}
                    value={formData.fatherOccupation}
                    placeholder="Enter father's occupation"
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {renderError("fatherOccupation")}
                </div>
                <div className='form-group'>
                  <label className='form-label'>Mother Occupation</label>
                  <input
                    type='text'
                    name='motherOccupation'
                    className={`form-input${fieldErrors.motherOccupation ? " input-error" : ""}`}
                    value={formData.motherOccupation}
                    placeholder="Enter mother's occupation"
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {renderError("motherOccupation")}
                </div>
              </div>

              <div className='form-row'>
                <div className='form-group col'>
                  <label className='form-label'>Mobile</label>

                  <input
                    type='text'
                    name='mobile'
                    className={`form-input${fieldErrors.mobile ? " input-error" : ""}`}
                    value={formData.mobile}
                    placeholder='Enter Mobile Number'
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />

                  {renderError("mobile")}
                </div>
                <div className='form-group'>
                  <label className='form-label'>Hobbies</label>
                  <input
                    type='text'
                    name='hobbies'
                    className={`form-input${fieldErrors.hobbies ? " input-error" : ""}`}
                    value={formData.hobbies}
                    placeholder='Enter hobbies'
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {renderError("hobbies")}
                </div>
              </div>

              <div className='form-row'>
                <div className='form-group'>
                  <label className='form-label'>Total Kiraga Paavam</label>
                  <input
                    type='text'
                    name='kiraga'
                    className={`form-input${fieldErrors.kiraga ? " input-error" : ""}`}
                    value={formData.kiraga}
                    placeholder='Enter Total Kiraga Paavam'
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {renderError("kiraga")}
                </div>
                <SelectField
                  label='Sevvaai dosha for lakna'
                  name='sevvaiLakna'
                  options={SEVVAI_LAKNA_OPTIONS}
                  {...selectProps}
                />
              </div>

              <div className='form-row'>
                <SelectField label='Drinking Habit' name='drinking' options={DRINKING_HABIT_OPTIONS} {...selectProps} />
                <SelectField label='Smoking Habit' name='smoking' options={SMOKING_HABIT_OPTIONS} {...selectProps} />
              </div>

              <div className='form-row'>
                <SelectField label='Eating Habit' name='eating' options={EATING_HABIT_OPTIONS} {...selectProps} />
              </div>

              <div className='form-row'>
                <div className='form-group'>
                  <label className='form-label'>Horoscope</label>
                  <input
                    key={fileInputKey}
                    type='file'
                    name='horoscope'
                    className={fieldErrors.horoscope ? "input-error" : ""}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    accept='.jpg,.jpeg,.png'
                  />
                  {renderError("horoscope")}
                </div>

                <div className='form-group'>
                  <label className='form-label'>Photo</label>
                  <input
                    key={fileInputKey}
                    type='file'
                    name='photo'
                    className={fieldErrors.photo ? "input-error" : ""}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    accept='.jpg,.jpeg,.png'
                  />
                  {renderError("photo")}
                </div>
              </div>

              <div className='form-group'>
                <label className='form-label'>About You</label>
                <textarea
                  name='about'
                  className={`form-input${fieldErrors.about ? " input-error" : ""}`}
                  value={formData.about}
                  placeholder='Tell us about yourself...'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  rows={4}
                />
                {renderError("about")}
              </div>

              <div className='form-group'>
                <label className='form-label'>About Your Partner</label>
                <textarea
                  name='partner'
                  className={`form-input${fieldErrors.partner ? " input-error" : ""}`}
                  value={formData.partner}
                  placeholder='Describe your ideal partner...'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  rows={4}
                />
                {renderError("partner")}
              </div>
            </div>
          </div>

          <div className='terms-checkbox-wrapper'>
            <label className='terms-checkbox-label'>
              <input type='checkbox' checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} />

              <span>
                I agree to the{" "}
                <a href='/terms-and-conditions' target='_blank' rel='noopener noreferrer'>
                  Terms & Conditions
                </a>{" "}
                and{" "}
                <a href='/privacy-policy' target='_blank' rel='noopener noreferrer'>
                  Privacy Policy
                </a>
              </span>
            </label>
          </div>

          <div className='button-wrapper'>
            <Button variant='contained' className='register-btn' onClick={handleSubmit} disabled={loading}>
              {loading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1, color: "#fff" }} />
                  REGISTERING...
                </>
              ) : (
                "Register Free"
              )}
            </Button>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default RegisterCard;
